import { dayProblem, hoursSummary, isAllDay, officeHours, rangesOn, WEEKDAYS } from "@/lib/calendars";
import type { LineStaffingStatus, StaffingAlert, StaffingAlertInput } from "@/lib/types";

/**
 * A line's staffing alert (ACL-567, ADR-177 in `platform`): who is told when an inbound line has
 * nobody to take its next call inside its staffed hours, and how the floor says it.
 *
 * Nobody to take the next call is every rep signed in to the line on a break, with a phone that
 * dropped, or on My calls only there. A rep on a call is somebody: they take the next caller when
 * they are free. The server decides it (`src/staffing.py`); these say it.
 */

/**
 * How long a line may sit empty before anybody is told, as the dialog asks it ("Alert after the
 * line is empty for"). A caller already waiting is told right away anyway.
 */
export const WAIT_CHOICES: readonly { seconds: number; label: string }[] = [
  { seconds: 0, label: "No wait, right away" },
  { seconds: 60, label: "1 minute" },
  { seconds: 120, label: "2 minutes" },
  { seconds: 180, label: "3 minutes" },
  { seconds: 300, label: "5 minutes" },
  { seconds: 600, label: "10 minutes" },
  { seconds: 900, label: "15 minutes" },
  { seconds: 1800, label: "30 minutes" },
];

/** A wait in words: "2 minutes", "right away". */
export function waitText(seconds: number): string {
  if (seconds <= 0) return "right away";
  const known = WAIT_CHOICES.find((c) => c.seconds === seconds);
  if (known) return known.label;
  return seconds % 60 === 0 ? `${seconds / 60} minutes` : `${seconds} seconds`;
}

/** The US zones by the name people use for them; any other zone as it is spelled. */
const ZONE_NAMES: Record<string, string> = {
  "America/New_York": "Eastern time (New York)",
  "America/Chicago": "Central time (Chicago)",
  "America/Denver": "Mountain time (Denver)",
  "America/Phoenix": "Arizona time (Phoenix)",
  "America/Los_Angeles": "Pacific time (Los Angeles)",
  "America/Anchorage": "Alaska time (Anchorage)",
  "Pacific/Honolulu": "Hawaii time (Honolulu)",
};

export function zoneLabel(zone: string): string {
  return ZONE_NAMES[zone] ?? zone.replace(/_/g, " ");
}

/** Whether the hours are every day, all day (the "All day, every day" choice). */
export function allTheTime(hours: StaffingAlertInput["hours"]): boolean {
  return isAllDay(hours);
}

/**
 * The whole alert in one sentence, under the dialog's form, so an admin reads what they set:
 * "When nobody on lhpark_ca-inbound can take a call for 2 minutes, on weekdays 9:00 AM to 5:00 PM
 * (Pacific time), #inbound-call in Slack and the admins and supervisors are told."
 */
export function draftSentence(draft: StaffingAlertInput, line: string): string {
  if (!draft.enabled) return "Off: nobody is told when this line is empty.";
  const when = allTheTime(draft.hours)
    ? "at any time of day"
    : `on ${lowerFirst(hoursSummary(draft.hours, "12h", "mon"))} (${zoneLabel(draft.timezone).replace(/ \(.*\)$/, "")})`;
  const wait = draft.wait_seconds > 0 ? ` for ${waitText(draft.wait_seconds)}` : "";
  const who =
    draft.slack_connection_id !== null && draft.slack_channel_id
      ? `#${draft.slack_channel_name ?? draft.slack_channel_id} in Slack and the admins and supervisors are told`
      : "the admins and supervisors are told in their notifications";
  return `When nobody on ${line} can take a call${wait}, ${when}, ${who}.`;
}

function lowerFirst(text: string): string {
  return text.replace(/^(Weekdays|Weekends|Every day)/, (w) => w.toLowerCase());
}

/**
 * An example of the Slack alert, for the dialog's preview: two reps signed in, both on a break.
 * The server writes the real one (`staffing.text` in core-api), in these words.
 */
export function slackPreview(lineTitle: string, waitSeconds: number): string[] {
  const minutes = Math.max(1, Math.round(waitSeconds / 60));
  return [
    `🚨 Nobody is Ready on ${lineTitle}`,
    `Nobody signed in to it can take a call, for ${minutes} min now.`,
    "Signed in: 2 · on a break: 2 · Nobody waiting",
  ];
}

export const DEFAULT_WAIT_SECONDS = 120;

/** Why Slack did not take the last message, in words. */
export function slackProblemText(code: string): string {
  switch (code) {
    case "slack_off":
      return "the Slack connection is turned off";
    case "channel_not_found":
      return "Slack cannot find the channel";
    case "not_in_channel":
      return "the app is not in the channel";
    case "is_archived":
      return "the channel is archived";
    case "invalid_auth":
    case "token_revoked":
    case "account_inactive":
    case "missing_scope":
      return "Slack refused the connection's token";
    case "slack_busy":
      return "Slack asked to wait";
    case "slack_unreachable":
      return "Slack could not be reached";
    default:
      return `Slack said ${code}`;
  }
}

/** A time of day as the viewer's clock reads it: "10:44 AM". `timeZone` is for tests. */
export function clockTime(iso: string, timeZone?: string): string {
  return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone });
}

export interface FloorNotice {
  title: string;
  detail: string;
}

/**
 * What the floor says above Now while the line has nobody to take its next call inside its staffed
 * hours, or null while somebody can (or the alert is off, or the hours are over). The count is the
 * floor's own live one; the times are the server's sweep's.
 */
export function floorNotice(
  status: LineStaffingStatus | null | undefined,
  now: number,
  timeZone?: string,
): FloorNotice | null {
  if (!status || !status.in_hours || status.can_take > 0) return null;
  const at = (iso: string) => clockTime(iso, timeZone);
  const title = status.unstaffed_since
    ? `Nobody can take calls on this line since ${at(status.unstaffed_since)}`
    : "Nobody can take calls on this line";
  const channel = status.slack_channel_name ? `#${status.slack_channel_name}` : null;
  let detail: string;
  if (status.alerted_at) {
    if (status.told_at && channel) {
      detail = `Slack was told at ${at(status.told_at)} in ${channel}, and the admins and supervisors in their notifications.`;
    } else if (channel && status.problem) {
      detail = `Slack could not be told: ${slackProblemText(status.problem)}. The admins and supervisors were told at ${at(status.alerted_at)} in their notifications.`;
    } else if (channel) {
      detail = `The admins and supervisors were told at ${at(status.alerted_at)}; Slack is being told now.`;
    } else {
      detail = `The admins and supervisors were told at ${at(status.alerted_at)} in their notifications.`;
    }
  } else {
    const who = channel ? `${channel} and the admins and supervisors are` : "The admins and supervisors are";
    const when = status.alert_at && Date.parse(status.alert_at) > now ? `at ${at(status.alert_at)}` : "now";
    detail = `${who} told ${when}, unless a rep is Ready first.`;
  }
  return { title, detail: `${detail} Reps on a break, signed out, or with their phone disconnected cannot take a call.` };
}

/** The alert in a few words, for the line's row: "Weekdays, 9:00 AM to 5:00 PM · #inbound-call". */
export function alertSummary(alert: StaffingAlert): string {
  if (!alert.enabled) return "Off";
  const channel = alert.slack_connection_id !== null ? alert.slack_channel_name ?? alert.slack_channel_id : null;
  const where = channel ? `#${channel}` : "Notifications only";
  return `${hoursSummary(alert.hours, "12h", "mon")} · ${where}`;
}

/** The viewer's own zone, when the browser says one. */
function browserZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

/**
 * What the dialog starts from: the saved alert, or for a line that has none, weekdays 9 to 5 on the
 * campaign's clock (the server's default), else the viewer's own, else Los Angeles.
 */
export function startingDraft(alert: StaffingAlert): StaffingAlertInput {
  return {
    enabled: alert.enabled,
    slack_connection_id: alert.slack_connection_id,
    slack_channel_id: alert.slack_channel_id,
    slack_channel_name: alert.slack_channel_name,
    timezone: alert.timezone ?? browserZone() ?? "America/Los_Angeles",
    hours: alert.hours.length > 0 ? alert.hours : officeHours(),
    wait_seconds: alert.wait_seconds,
  };
}

/**
 * What is saved. An alert turned off keeps what was set, so turning it on again brings it back, but
 * a Slack choice left without its channel is dropped: the hidden half would only block the save.
 */
export function toSave(draft: StaffingAlertInput): StaffingAlertInput {
  if (draft.enabled || draft.slack_channel_id || draft.slack_connection_id === null) return draft;
  return { ...draft, slack_connection_id: null, slack_channel_id: null, slack_channel_name: null };
}

/**
 * Why the draft cannot be saved, or null. The server says the same, and more. Turned off, only what
 * is kept can be wrong: a day whose ranges overlap.
 */
export function draftProblem(draft: StaffingAlertInput): string | null {
  if (draft.enabled && draft.slack_connection_id !== null && !draft.slack_channel_id) {
    return "Choose a Slack channel, or turn Slack off.";
  }
  for (const day of WEEKDAYS) {
    const problem = dayProblem(rangesOn(draft.hours, day));
    if (problem) return problem;
  }
  if (draft.enabled && draft.hours.length === 0) {
    return "Choose at least one day the line should have people.";
  }
  return null;
}
